/**
 * VersionBadge — muestra la versión actual del OS y avisa si hay una nueva.
 *
 * - Lee `import.meta.env.VITE_APP_VERSION` (inyectado en build desde package.json)
 * - Pollea `/api/version` cada 5 min
 * - Si la versión del backend != frontend → toast "Nueva versión · Recargar"
 * - Click en el badge → muestra info de build + link a CHANGELOG
 *
 * Posición: footer del Sidebar (junto al user menu).
 */
import { useEffect, useState } from "react";
import { Sparkles } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { useToast } from "./Toast.jsx";
import { cn } from "../lib/utils.js";
import { APP_VERSION as FRONTEND_VERSION, APP_ENV as ENV, VERSION_PRODUCTO, fullVersion } from "../lib/version.js";

const POLL_MS = 5 * 60 * 1000; // 5 min

export default function VersionBadge({ collapsed = false }) {
  const [backendVer, setBackendVer] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [alreadyToasted, setAlreadyToasted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let stopped = false;

    async function check() {
      try {
        const r = await fetch("/api/version", { credentials: "include" });
        if (!r.ok) return;
        const data = await r.json();
        if (stopped) return;
        setBackendVer(data.version);

        // Si la versión del backend cambió respecto al frontend cargado → nueva versión
        if (data.version && data.version !== FRONTEND_VERSION) {
          setHasUpdate(true);
          if (!alreadyToasted) {
            setAlreadyToasted(true);
            toast.info(`Nueva versión disponible (v${data.version})`, {
              description: "Recargá para verla. Click en el botón.",
              duration: 0, // sticky
              action: {
                label: "Recargar ahora",
                onClick: () => window.location.reload(),
              },
            });
          }
        }
      } catch (e) {
        // Silent fail — no es crítico
      }
    }

    check();
    const id = setInterval(check, POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [alreadyToasted, toast]);

  const versionLabel = ENV === "prod" ? `v${VERSION_PRODUCTO}` : `v${VERSION_PRODUCTO} · ${ENV}`;
  const colorCls = hasUpdate
    ? "bg-[#FEF3C7] text-[#92400E]"
    : ENV === "canary"
    ? "bg-[#F3EEFF] text-[#6D28D9]"
    : "bg-transparent text-[#A6ABB6]";

  if (collapsed) {
    return (
      <div
        className={cn(
          "w-full flex items-center justify-center px-1 py-1 rounded-full text-[12px] font-semibold",
          colorCls
        )}
        title={hasUpdate ? `Nueva versión v${backendVer} disponible — click para recargar` : versionLabel}
        onClick={hasUpdate ? () => window.location.reload() : undefined}
        style={{ cursor: hasUpdate ? "pointer" : "default" }}
      >
        {hasUpdate ? <Sparkles className="h-3 w-3" /> : `v${VERSION_PRODUCTO}`}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "w-full flex items-center justify-between px-3 py-1 rounded-full text-[12px] font-semibold",
        colorCls
      )}
      title={
        hasUpdate
          ? `Nueva versión v${backendVer} disponible — click para recargar`
          : `Frontend ${FRONTEND_VERSION} · Backend ${backendVer || "—"} · ${ENV}`
      }
      onClick={hasUpdate ? () => window.location.reload() : undefined}
      style={{ cursor: hasUpdate ? "pointer" : "default" }}
    >
      <span>{versionLabel}</span>
      {hasUpdate && (
        <span className="flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          <span className="text-[12px]">recargar</span>
        </span>
      )}
    </div>
  );
}
